/**
 * Forex USD Conversion Utility
 * 
 * Handles proper USD conversion for forex pairs.
 * 
 * Key concept:
 * - XXX/USD pairs: 1 lot = 100,000 base currency. Price IS USD per unit.
 *   Notional(USD) = price × qty × lotSize
 *   e.g., EURUSD: 1.16428 × 0.01 × 100,000 = $1,164.28
 * 
 * - USD/XXX pairs: 1 lot = 100,000 USD. Price is quote per 1 USD.
 *   Notional(USD) = qty × lotSize (price NOT used, lot is already USD)
 *   P&L in quote currency = (exitPrice - entryPrice) × qty × lotSize
 *   Then convert to USD: divide by exitPrice
 *   e.g., USDJPY: 0.01 × 100,000 = $1,000 notional
 * 
 * - Cross pairs: 1 lot = 100,000 BASE currency. Need reference rate for USD.
 *   Notional(USD) = refRate × qty × lotSize (price NOT used)
 *   e.g., GBPJPY: 1.34615 × 0.01 × 100,000 = $1,346.15
 */

/**
 * Check if a forex pair is USD/XXX type (base currency is USD)
 */
const isUSDBased = (instrumentId) => {
  if (!instrumentId) return false;
  return instrumentId.startsWith('USD') && 
         instrumentId !== 'EURUSD' && 
         instrumentId !== 'GBPUSD' && 
         instrumentId !== 'AUDUSD' && 
         instrumentId !== 'NZDUSD';
};

/**
 * Check if a forex pair is XXX/USD type (quote currency is USD)
 */
const isUSDQuoted = (instrumentId) => {
  return instrumentId === 'EURUSD' || instrumentId === 'GBPUSD' || 
         instrumentId === 'AUDUSD' || instrumentId === 'NZDUSD';
};

/**
 * Get the reference rate (USD per 1 unit of base currency) for cross pairs
 */
const getCrossRefRate = (instrumentId, marketData = {}) => {
  const getRef = (refId, fallback) => {
    const refData = marketData[refId];
    return refData?.price || fallback;
  };

  if (instrumentId.startsWith('EUR')) return getRef('EURUSD', 1.08);
  if (instrumentId.startsWith('GBP')) return getRef('GBPUSD', 1.27);
  if (instrumentId.startsWith('AUD')) return getRef('AUDUSD', 0.66);
  if (instrumentId.startsWith('NZD')) return getRef('NZDUSD', 0.60);
  if (instrumentId.startsWith('CAD')) return 1 / getRef('USDCAD', 1.36);
  if (instrumentId.startsWith('CHF')) return 1 / getRef('USDCHF', 0.88);
  
  return 1; // fallback
};

/**
 * Get the USD-equivalent price for a forex instrument.
 * Used ONLY for P&L calculations (price difference in USD).
 * 
 * For XXX/USD: returns price (already USD)
 * For USD/XXX: returns price (raw quote, division by exitPrice happens in calcForexPnl)
 * For cross: returns price × refRate (USD per unit of base)
 */
export const getForexUSDPrice = (instrumentId, price, category, marketData = {}) => {
  if (category !== 'forex') return price;
  if (!price || price <= 0) return price;
  if (!instrumentId) return price;

  // XXX/USD pairs: price IS already USD per unit of base
  if (isUSDQuoted(instrumentId)) {
    return price;
  }

  // USD/XXX pairs: return raw price — P&L conversion to USD happens in calcForexPnl
  // by dividing the quote-currency P&L by the exit price
  if (isUSDBased(instrumentId)) {
    return price;
  }

  // Cross pairs: multiply by reference rate to get USD per unit of base
  const refRate = getCrossRefRate(instrumentId, marketData);
  return price * refRate;
};

/**
 * Calculate NOTIONAL VALUE in USD for margin calculation.
 * THIS IS THE KEY FUNCTION for correct margin.
 * 
 * @param {string} instrumentId 
 * @param {number} price - Raw price (from market data)
 * @param {number} quantity - Number of lots
 * @param {string} category 
 * @param {number} lotSize - Lot size multiplier (100,000 for forex)
 * @param {object} marketData 
 * @returns {number} Notional value in USD
 */
export const getNotionalUSD = (instrumentId, price, quantity, category, lotSize, marketData = {}) => {
  if (category !== 'forex') {
    // Non-forex: standard calculation
    return price * quantity * lotSize;
  }

  // USD/XXX pairs: lot is ALREADY in USD (1 lot = 100,000 USD)
  // Price is quote per 1 USD, NOT used for notional
  // e.g., USDJPY: 0.01 lots = 0.01 × 100,000 = $1,000 notional
  if (isUSDBased(instrumentId)) {
    return quantity * lotSize;
  }

  // XXX/USD pairs: price gives USD per unit
  // e.g., EURUSD: 1.16428 × 0.01 × 100,000 = $1,164.28
  if (isUSDQuoted(instrumentId)) {
    return price * quantity * lotSize;
  }

  // Cross pairs: lot is in BASE currency, need ref rate for USD
  // PRICE IS NOT USED — only the base currency's USD value matters
  // e.g., GBPJPY: 1.34615 × 0.01 × 100,000 = $1,346.15
  const refRate = getCrossRefRate(instrumentId, marketData);
  return refRate * quantity * lotSize;
};

/**
 * Calculate P&L in USD for a forex position.
 * 
 * For USD/XXX pairs: P&L is calculated in quote currency first, then divided by exitPrice to get USD.
 * For all other pairs: standard (exit - entry) × qty × lotSize using USD prices.
 * 
 * @param {boolean} isShort - True if short position
 * @param {number} exitPrice - Raw exit price (current market price)
 * @param {number} entryPrice - Raw entry price
 * @param {number} quantity - Number of lots
 * @param {string} instrumentId 
 * @param {string} category 
 * @param {number} lotSize - Lot size multiplier
 * @param {object} marketData 
 * @returns {number} P&L in USD
 */
export const calcForexPnl = (isShort, exitPrice, entryPrice, quantity, instrumentId, category, lotSize, marketData = {}) => {
  if (category !== 'forex') {
    return isShort
      ? (entryPrice - exitPrice) * quantity * lotSize
      : (exitPrice - entryPrice) * quantity * lotSize;
  }

  // For USD/XXX pairs: P&L in quote currency, then divide by exitPrice for USD
  if (isUSDBased(instrumentId)) {
    const pnlInQuote = isShort
      ? (entryPrice - exitPrice) * quantity * lotSize
      : (exitPrice - entryPrice) * quantity * lotSize;
    
    // Convert quote currency P&L to USD by dividing by exit price
    return pnlInQuote / exitPrice;
  }

  // For XXX/USD and cross pairs: use USD-equivalent prices
  const usdExit = getForexUSDPrice(instrumentId, exitPrice, category, marketData);
  const usdEntry = getForexUSDPrice(instrumentId, entryPrice, category, marketData);
  
  return isShort
    ? (usdEntry - usdExit) * quantity * lotSize
    : (usdExit - usdEntry) * quantity * lotSize;
};